/**
 * A labelled form control.
 *
 * Exists so that every input in the app gets a real `<label for>`, an
 * `aria-describedby` for its hint, and `aria-invalid` plus a visible message
 * when it is wrong. Doing that by hand at each call site is how half of them
 * end up without it.
 *
 * The validation shown here is a courtesy - it tells the user about a problem
 * before a round trip. The backend's validation is the one that protects
 * anything, and it runs regardless of what this component allows.
 */

import { useId, type ReactNode } from "react";

export function Field({
  label,
  required = false,
  optional = false,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  /** Says so in words. Without it, an unmarked field reads as required. */
  optional?: boolean;
  hint?: string;
  error?: string | null;
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
    required: boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={error ? "field field--invalid" : "field"}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
        {optional && <span className="field__optional">İsteğe bağlı</span>}
      </label>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
        required,
      })}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
